import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { siteConfig } from "@/config";
import PageTransition from "@/components/PageTransition";
import AnimatedText from "@/components/AnimatedText";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useState as useCopyState } from "react";
import {
  IconCopy,
  IconCheck,
  IconClock,
} from "@tabler/icons-react";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

// Throttle function for performance
function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): T {
  let inThrottle: boolean;
  return ((...args: any[]) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  }) as T;
}

function stripFrontmatter(md: string): string {
  if (md.startsWith("---")) {
    const end = md.indexOf("---", 3);
    if (end !== -1) {
      return md.slice(end + 3).trimStart();
    }
  }
  return md;
}

const TutorialDetail = () => {
  const { tutorialId } = useParams<{ tutorialId: string }>();
  const navigate = useNavigate();
  const [tutorial, setTutorial] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [markdown, setMarkdown] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<string>("");
  const [readingProgress, setReadingProgress] = useState(0);

  // Memoized scroll handler for better performance
  const handleScroll = useCallback(
    throttle(() => {
      // Reading progress calculation
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setReadingProgress(Math.min(progress, 100));

      // Active section detection - only run when needed
      const headings = document.querySelectorAll("h1[id], h2[id], h3[id]");
      let current = "";

      for (let i = 0; i < headings.length; i++) {
        const heading = headings[i] as HTMLElement;
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 100) {
          current = heading.id;
        }
      }

      setActiveSection(current);
    }, 16), // ~60fps throttling
    []
  );

  // Reading progress calculation with throttling
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const found = siteConfig.tutorials.items.find(
      (t: any) => t.slug === tutorialId
    );
    if (!found) {
      navigate("/not-found");
      return;
    }
    setTutorial(found);
    setIsVisible(true);
    document.title = `${found.title} | Tutorial`;
    setLoading(true);
    fetch(found.content)
      .then((res) => res.text())
      .then((md) => {
        setMarkdown(stripFrontmatter(md));
        setLoading(false);
      });
  }, [tutorialId, navigate]);

  // Memoized TOC generation
  const generateToc = useCallback((md: string) => {
    const lines = md.split("\n").filter((line) => !/^---$/.test(line.trim()));
    const tocItems: TocItem[] = [];
    lines.forEach((line: string) => {
      const match = line.match(/^(#{1,3})\s+(.*)/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        if (text.toLowerCase() !== "table of contents") {
          tocItems.push({ id, text, level });
        }
      }
    });
    return tocItems;
  }, []);

  useEffect(() => {
    if (!markdown) return;
    const tocItems = generateToc(markdown);
    setToc(tocItems);
  }, [markdown, generateToc]);

  // Memoized markdown components for better performance
  const markdownComponents = useMemo(
    () => ({
      code({ node, inline, className, children, ...props }: any) {
        const match = /language-(\w+)/.exec(className || "");
        const codeString = String(children).replace(/\n$/, "");

        if (!inline) {
          const [isHovered, setIsHovered] = useCopyState(false);
          return (
            <div
              className="relative my-8 rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-gradient-to-br from-gray-900 to-black group/codeblock"
              tabIndex={0}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onFocus={() => setIsHovered(true)}
              onBlur={() => setIsHovered(false)}
              style={{ outline: "none" }}
            >
              {/* Code header */}
              <div className="flex items-center justify-between px-6 py-3 bg-white/5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                  </div>
                  <span className="text-sm text-gray-400 font-mono">
                    {match ? match[1] : "code"}
                  </span>
                </div>
              </div>

              <CopyButton code={codeString} show={isHovered} />

              <SyntaxHighlighter
                style={vscDarkPlus as any}
                language={match ? match[1] : undefined}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  padding: "1.5rem",
                  background: "transparent",
                  fontSize: "0.9rem",
                  lineHeight: "1.6",
                }}
                {...props}
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          );
        }

        return (
          <code className="px-2 py-1 rounded-md bg-white/10 text-blue-300 font-mono text-sm border border-white/10">
            {children}
          </code>
        );
      },
      h1({ children }: any) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <h1
            id={id}
            className="text-3xl md:text-4xl font-semibold mb-6 mt-10 leading-tight font-sans"
          >
            {children}
          </h1>
        );
      },
      h2({ children }: any) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <h2
            id={id}
            className="text-2xl md:text-3xl font-medium mb-4 mt-8 leading-snug font-sans"
          >
            {children}
          </h2>
        );
      },
      h3({ children }: any) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return (
          <h3
            id={id}
            className="text-xl md:text-2xl font-normal mb-3 mt-6 leading-snug font-sans"
          >
            {children}
          </h3>
        );
      },
      p({ children }: any) {
        return (
          <p className="mb-6 mt-4 text-lg leading-relaxed text-gray-300">
            {children}
          </p>
        );
      },
      ul({ children }: any) {
        return <ul className="list-none mb-6 mt-4 space-y-2">{children}</ul>;
      },
      li({ children }: any) {
        return (
          <li className="flex items-start gap-3 text-gray-300 leading-relaxed">
            <span className="w-2 h-2 rounded-full bg-blue-400 mt-2.5 flex-shrink-0"></span>
            <span>{children}</span>
          </li>
        );
      },
      ol({ children }: any) {
        return (
          <ol className="list-decimal list-inside mb-6 mt-4 space-y-2 text-gray-300">
            {children}
          </ol>
        );
      },
      blockquote({ children }: any) {
        return (
          <blockquote className="relative border-l-4 border-blue-500 pl-6 py-4 my-8 bg-blue-500/5 rounded-r-xl">
            <div className="absolute top-4 left-4 text-blue-500/40 text-6xl font-serif leading-none">
              "
            </div>
            <div className="relative text-blue-100 italic text-lg leading-relaxed">
              {children}
            </div>
          </blockquote>
        );
      },
      strong({ children }: any) {
        return (
          <strong className="font-semibold text-blue-300">{children}</strong>
        );
      },
      a({ children, href }: any) {
        const isExternal = href && /^(http|https):\/\//.test(href);
        return (
          <a
            href={href}
            className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/50 hover:decoration-blue-300 underline-offset-2 transition-colors"
            {...(isExternal
              ? {
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {})}
          >
            {children}
          </a>
        );
      },
    }),
    []
  );

  // Enhanced Copy Button with better animations
  function CopyButton({ code, show }: { code: string; show: boolean }) {
    const [copied, setCopied] = useCopyState(false);
    const [showTooltip, setShowTooltip] = useCopyState(false);
    return (
      <div
        className={cn(
          "absolute top-4 right-4 z-[100] flex items-center transition-all duration-300 transform",
          !show && "opacity-0 pointer-events-none scale-90"
        )}
      >
        <button
          className={cn(
            "group relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-xl border border-white/20 shadow-2xl transition-all duration-300",
            "hover:from-white/30 hover:to-white/20 hover:border-white/40 hover:shadow-blue-500/20 hover:scale-110 active:scale-95",
            "focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          )}
          onClick={() => {
            navigator.clipboard.writeText(code);
            setCopied(true);
            setShowTooltip(true);
            setTimeout(() => setCopied(false), 1500);
            setTimeout(() => setShowTooltip(false), 1700);
          }}
          aria-label="Copy code"
          type="button"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
        >
          <span className="relative flex items-center justify-center">
            <span
              className={cn(
                "absolute transition-all duration-300 ease-out",
                copied
                  ? "opacity-0 scale-75 rotate-180"
                  : "opacity-100 scale-100 rotate-0"
              )}
            >
              <IconCopy size={18} className="text-white drop-shadow-lg" />
            </span>
            <span
              className={cn(
                "absolute transition-all duration-300 ease-out",
                copied
                  ? "opacity-100 scale-110 rotate-0"
                  : "opacity-0 scale-75 rotate-180"
              )}
            >
              <IconCheck size={18} className="text-green-400 drop-shadow-lg" />
            </span>
          </span>

          {/* Enhanced tooltip */}
          <span
            className={cn(
              "absolute right-14 top-1/2 -translate-y-1/2 px-3 py-2 text-xs font-medium rounded-lg bg-black/90 text-white shadow-2xl border border-white/10 opacity-0 pointer-events-none select-none transition-all duration-300 whitespace-nowrap backdrop-blur-xl",
              showTooltip && "opacity-100 translate-x-0",
              !showTooltip && "translate-x-2"
            )}
            style={{ zIndex: 9999 }}
          >
            {copied ? "✨ Copied!" : "📋 Copy code"}
            <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-l-4 border-l-black/90 border-t-2 border-b-2 border-t-transparent border-b-transparent"></div>
          </span>
        </button>
      </div>
    );
  }

  if (!tutorial) return null;

  return (
    <PageTransition>
      <div className="bg-black text-white min-h-screen relative overflow-hidden">
        {/* Animated background elements */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-pink-900/10"></div>
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="noise-bg opacity-20"></div>
        </div>

        {/* Reading progress bar - Fixed and always visible */}
        <div className="fixed top-0 left-0 right-0 z-[100] h-1 bg-black/50 backdrop-blur-sm">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-out shadow-lg shadow-blue-500/50"
            style={{ width: `${readingProgress}%` }}
          ></div>
        </div>

        <header className="relative z-50">
          <Navbar />
        </header>

        {/* Table of Contents Side Panel: fixed to right of viewport, always visible on desktop */}
        <aside className="hidden md:block fixed top-24 right-8 w-[360px] max-w-[30vw] h-[calc(100vh-7rem)] overflow-y-auto z-[60]">
          <div className="border border-white/20 rounded-xl p-6 bg-black/30 backdrop-blur-sm h-full flex flex-col">
            <h2 className="text-lg font-display mb-4 tracking-wide">
              Table of Contents
            </h2>
            <ScrollArea className="h-full pr-3 overflow-y-auto">
              <ul className="space-y-2">
                {toc.map((item, i) => (
                  <li
                    key={i}
                    className={cn(
                      "transition-all duration-200 cursor-pointer hover:text-blue-400",
                      item.level === 1 && "font-bold text-base",
                      item.level === 2 && "pl-4 text-sm",
                      item.level === 3 && "pl-8 text-xs"
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(item.id);
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {item.text}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </div>
        </aside>
        <main
          id="main-content"
          className="relative z-10 container mx-auto px-4 pt-28 md:pt-36 pb-20 md:pb-32 flex flex-col md:flex-row gap-12"
        >
          {/* Main Content: 70% width on desktop, add right margin so it doesn't go under ToC */}
          <div className="w-full md:w-[70%] min-w-0 md:mr-[370px]">
            <Link
              to="/tutorials"
              className={cn(
                "inline-flex items-center gap-2 mb-12 group transition-all duration-500 transform",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-4"
              )}
            >
              <span className="uppercase text-sm tracking-widest relative overflow-hidden group-hover:after:scale-x-100 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:-bottom-1 after:left-0 after:bg-white after:origin-bottom-right after:transition-transform after:duration-300">
                Back to Tutorials
              </span>
            </Link>
            <div className="grid grid-cols-1 gap-12">
              {/* Main Content */}
              <div
                className={cn(
                  "transition-all duration-700",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                )}
              >
                {/* Enhanced header section */}
                <div className="relative mb-12">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur-xl"></div>
                  <div className="relative">
                    <h1 className="font-display text-4xl md:text-7xl tracking-tight mb-6 text-white leading-tight">
                      <AnimatedText text={tutorial.title} delay={100} />
                    </h1>

                    {/* Enhanced badges */}
                    <div className="flex flex-wrap gap-3 mb-8">
                      <Badge className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-200 px-4 py-2 text-sm font-medium">
                        {tutorial.difficulty}
                      </Badge>
                      <Badge className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 border-purple-500/30 text-purple-200 px-4 py-2 text-sm font-medium flex items-center gap-2">
                        <IconClock size={14} />
                        {tutorial.duration} min read
                      </Badge>
                      {tutorial.techStack.map((tech: string, i: number) => (
                        <Badge
                          key={i}
                          className="bg-white/5 border-white/20 text-gray-300 hover:bg-white/10 transition-colors px-3 py-2 text-sm"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </div>

                    <div className="text-gray-300 mb-8 text-lg leading-relaxed max-w-3xl">
                      {tutorial.description}
                    </div>
                  </div>
                </div>

                <Separator className="mb-12 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                {/* Enhanced content area */}
                <div className="prose prose-invert max-w-none custom-markdown">
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="relative">
                        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin animation-delay-150"></div>
                      </div>
                    </div>
                  ) : (
                    <ReactMarkdown
                      components={markdownComponents}
                      children={markdown}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="relative z-10">
          <Footer />
        </footer>
      </div>
    </PageTransition>
  );
};

export default TutorialDetail;
